import React, { useState, useRef, useEffect } from 'react';
import CommonModal from './CommonModal';

const GOOGLE_MAPS_API_KEY = 'AIzaSyCb6XBv9tnLgqhPfWHBlzokjXagwfX3vtk'; 

const initialFormState = {
  name: '',
  phone: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  country: '',
  postal_code: '',
  latitude: '',
  longitude: '',
  full_address: ''
};

const AddDeliveryAddressModal = ({ isOpen, onClose, onSubmit, customerInfo }) => {
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const addressInputRef = useRef(null);

  const loadGoogleMapsScript = () => {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve();
        return;
      }
      const existing = document.getElementById('googleMapsScript');
      if (!existing) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.id = 'googleMapsScript';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = (err) => reject(err);
        document.body.appendChild(script);
      } else {
        existing.onload = () => resolve();
      }
    });
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `.pac-container { z-index: 8000 !important; }`;
    document.head.appendChild(style);
  
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (isOpen && customerInfo) {
      setFormData((prev) => ({
        ...prev,
        name: `${customerInfo.first_name || ''} ${customerInfo.last_name || ''}`.trim(),
        phone: customerInfo.mobile || ''
      }));
    }
  }, [isOpen, customerInfo]);

  useEffect(() => {
    if (!isOpen) return;

    const initAutocomplete = async () => {
      try {
        await loadGoogleMapsScript();

        if (!addressInputRef.current) {
          console.warn('addressInputRef.current is null');
          return;
        }


        const autocomplete = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            fields: ['address_components',  'geometry','formatted_address'],
            componentRestrictions: { country: 'au' },
            types: ['geocode']
          }
        );


        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (!place.address_components) return;
        
          setFormData((prev) => {
            const updated = { ...prev };
        
            updated.address_line_1 = addressInputRef.current.value;
            updated.latitude = place.geometry?.location?.lat();
            updated.longitude = place.geometry?.location?.lng();
            updated.full_address = place.formatted_address;
        
            const componentForm = {
              street_number: 'short_name',
              route: 'long_name',
              locality: 'long_name',
              administrative_area_level_1: 'long_name',
              country: 'long_name',
              postal_code: 'short_name'
            };
        
            place.address_components.forEach((component) => {
              const addressType = component.types[0];
              const val = component[componentForm[addressType]];
        
              switch (addressType) {
                case "locality":
                case "postal_town": 
                  updated.city = val;
                  break;
        
                case "administrative_area_level_1":
                  updated.state = val;
                  break;
        
                case "country":
                  updated.country = val;
                  break;
        
                case "postal_code":
                  updated.postal_code = val;
                  break;
              }
            });
        
            return updated;
          });
        });
      } catch (err) {
        console.error('Error loading Google Maps script or autocomplete init:', err);
      }
    };

    initAutocomplete();
  }, [isOpen]);

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.phone) newErrors.phone = 'Phone is required';
    if (!formData.address_line_1) newErrors.address_line_1 = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.postal_code) newErrors.postal_code = 'Postal code is required';
    return newErrors;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    const payload = {
      name: formData.name,
      mobile: formData.phone,
      address_line_1: formData.address_line_1,
      address_line_2: formData.address_line_2,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      postal_code: formData.postal_code,
      latitude: formData.latitude,
      longitude: formData.longitude,
      full_address: formData.full_address
    };

    const result = await onSubmit(payload);

    if (result.success) {
      setFormData(initialFormState);
      setErrors({});
      onClose();
    } else if (result.errors) {
      const backendToFormFieldMap = {
        mobile: 'phone', 
        address_line_1: 'address_line_1',
        name: 'name',
        city: 'city',
        state: 'state',
        country: 'country',
        postal_code: 'postal_code'
      };
      const formattedErrors = {};
      Object.keys(result.errors).forEach((key) => {
        const field = backendToFormFieldMap[key] || key; 
        formattedErrors[field] = Array.isArray(result.errors[key])
          ? result.errors[key][0]
          : result.errors[key];
      });
      setErrors(formattedErrors);
    }
  };

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} elId="addDeliveryAddressModal">
      <div className="modal-header">
        <h5 className="modal-title">Add Delivery Address</h5>
        <button type="button" className="btn-close" onClick={onClose}></button>
      </div>
      <div className="modal-body">
        <form className="formStyle">
          <div className="row g-3 g-lg-4">
            <div className="col-md-6">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                placeholder="Full Name"
              />
              {errors.name && <p className="text-danger">{errors.name}</p>}
            </div>
            <div className="col-md-6">
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                placeholder="Phone Number"
              />
              {errors.phone && <p className="text-danger">{errors.phone}</p>}
            </div>

            <div className="col-md-6">
              <input
                type="text"
                name="address_line_1"
                ref={addressInputRef}
                value={formData.address_line_1}
                onChange={handleInputChange}
                className={`form-control ${errors.address_line_1 ? 'is-invalid' : ''}`}
                placeholder="Start typing your address"
              />
              {errors.address_line_1 && <p className="text-danger">{errors.address_line_1}</p>}
              <p style={{ fontSize: '14px', color: '#9B9797' }}>
                Start typing your address and select from suggestions.
              </p>
            </div>
            <div className="col-md-6">
              <input
                type="text"
                name="address_line_2"
                value={formData.address_line_2}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Address Line 2 (Optional)"
              />
            </div>

            <div className="col-md-6">
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                placeholder="City"
              />
              {errors.city && <p className="text-danger">{errors.city}</p>}
            </div>
            <div className="col-md-6">
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className={`form-control ${errors.state ? 'is-invalid' : ''}`}
                placeholder="State"
              />
              {errors.state && <p className="text-danger">{errors.state}</p>}
            </div>

            <div className="col-md-6">
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className={`form-control ${errors.country ? 'is-invalid' : ''}`}
                placeholder="Country"
              />
              {errors.country && <p className="text-danger">{errors.country}</p>}
            </div>
            <div className="col-md-6">
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleInputChange}
                className={`form-control ${errors.postal_code ? 'is-invalid' : ''}`}
                placeholder="Postal Code"
              />
              {errors.postal_code && <p className="text-danger">{errors.postal_code}</p>}
            </div>
          </div>
        </form>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btnLight" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btnPrimary" onClick={handleSubmit}>
          Add Address
        </button>
      </div>
    </CommonModal>
  );
};

export default AddDeliveryAddressModal;
